import { useEffect, useRef, useState } from 'react';
import {
  getUserDialogues,
  openCloseDialogueBox,
  selectDialogue,
  updateDialogueMessage,
} from '../../../features/sessions/sessionDialogues';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { Dialogue, DialogueMember } from '../../../#interfaces/interfaces';
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Popover,
  styled,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import { UserAvatar } from '../../userAvatar/UserAvatar';
import { format } from 'date-fns';
import { getUserGenericConfig } from '../../../features/sessions/sessionConfig/sessionUserConfigurations';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const SmallBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    minWidth: 5,
    height: 12,
    borderRadius: '50%',
    background: 'linear-gradient(45deg, #8B0000, #FF1493)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
}));

export function MessagesToolbar() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const currentUser = useSelector((state: RootState) => state.session.currentUser);
  const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
  const dialoguesUnvisualized = useSelector(
    (state: RootState) => state.sessionUserConfig.userMoreInfo.dialoguesUnvisualized
  );
  const userDialogues = useSelector((state: RootState) => {
    const list = state.sessionDialogues.userDialogues;
    return [...list].sort((a, b) => {
      const lastA = a.dialogueMessages?.at(-1)?.createdAt || 0;
      const lastB = b.dialogueMessages?.at(-1)?.createdAt || 0;
      return new Date(lastB).getTime() - new Date(lastA).getTime();
    });
  });
  const selectedDialogues = useSelector(
    (state: RootState) => state.sessionDialogues.selectedDialogues
  );
  const [dialoguesPage, setDialoguesPage] = useState<number>(1);
  const dispatch = useDispatch<AppDispatch>();
  const loadingMore = useSelector(
    (state: RootState) => state.sessionDialogues.loadingMoreDialogueMessages
  );
  const listRef = useRef<HTMLDivElement | null>(null);
  const openedDialogueBoxes = useSelector(
    (state: RootState) => state.sessionDialogues.openedDialogueBox
  );

  const open = Boolean(anchorEl);

  function formatDate(date: string) {
    return format(new Date(date), 'dd MMM yyyy HH:mm');
  }

  useEffect(() => {
    if (!authHeaders) return;
    dispatch(getUserDialogues({ authHeaders, urlParams: { page: dialoguesPage } })).catch(console.error);
  }, [dialoguesPage]);

  useEffect(() => {
    if (!open) return;
    const container = listRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight && !loadingMore) {
        setDialoguesPage((prev) => prev + 1);
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [open, loadingMore]);

  async function handleSelectDialogue(userNickname: string) {
    setAnchorEl(null);
    if (!selectedDialogues.includes(userNickname)) {
      dispatch(selectDialogue({ nickname: userNickname }));
    }
  }

  async function handleOpenDialogue(userNickname: string | undefined) {
    dispatch(openCloseDialogueBox({ nickname: userNickname as string }));
  }

  async function handleReadLastMessage(dialogue: Dialogue) {
    if (authHeaders && currentUser?.nickname) {
      const received = dialogue.dialogueMessages?.filter((m) => m.senderId !== currentUser.id);
      const lastId = received?.at(-1)?.id;
      if (lastId) {
        const response = await dispatch(
          updateDialogueMessage({
            authHeaders,
            urlParams: { userNickname: currentUser.nickname, dialogueMessageId: lastId },
            requestBody: { visualized: true },
          })
        );
        if (response.meta.requestStatus === 'fulfilled') {
          await dispatch(getUserGenericConfig({ authHeaders, userNickname: currentUser.nickname }));
        }
      }
    }
  }

  const hasUnread = dialoguesUnvisualized && dialoguesUnvisualized > 0;

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        sx={{
          color: open ? TEXT_PRIMARY : TEXT_SECONDARY,
          position: 'relative',
          '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
        }}
        aria-label="Mensagens"
      >
        {hasUnread && (
          <Box sx={{
            position: 'absolute', top: 4, right: 4,
            bgcolor: BRAND_RED, color: '#fff',
            fontSize: 9, fontWeight: 700, lineHeight: 1,
            borderRadius: '4px', px: 0.4, py: 0.2, zIndex: 2,
            minWidth: 14, textAlign: 'center',
          }}>
            {dialoguesUnvisualized}
          </Box>
        )}
        {hasUnread ? <ChatBubbleIcon fontSize="small" /> : <ChatBubbleOutlineIcon fontSize="small" />}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{
          ref: listRef,
          elevation: 0,
          sx: {
            width: { xs: 260, sm: 320 },
            minHeight: 80,
            maxHeight: { xs: 260, sm: 320 },
            overflowY: 'auto',
            bgcolor: BG_SECTION,
            border: BORDER_SUBTLE,
            borderRadius: '8px',
            mt: 0.5,
          },
        }}
      >
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {userDialogues.length === 0 && !loadingMore && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" sx={{ color: TEXT_SECONDARY, fontSize: 13 }}>
                No messages yet
              </Typography>
            </Box>
          )}
          {userDialogues.map((dialogue: Dialogue, index: number) => {
            const lastMessage = dialogue.dialogueMessages?.at(-1);
            const isVisualized =
              lastMessage &&
              ((lastMessage.lastAlienMessageVisualized && lastMessage.senderId === currentUser?.id) ||
                (lastMessage.visualized && lastMessage.senderId !== currentUser?.id));

            const otherMember = dialogue.dialogueMembers?.find(
              (m: DialogueMember) => m.user?.nickname !== currentUser?.nickname
            );
            const otherNickname = otherMember?.user?.nickname;
            const avatarUrl = otherMember?.user?.userMoreInfo?.avatarUrl;
            const nickname = otherMember?.user?.nickname || 'Unknown';

            const handleClick = () => {
              if (otherNickname) {
                handleReadLastMessage(dialogue);
                handleSelectDialogue(otherNickname);
                if (!openedDialogueBoxes.some((d) => d.id === dialogue.id)) {
                  handleOpenDialogue(otherNickname);
                }
              }
            };

            return (
              <Box
                key={index}
                component="li"
                onClick={handleClick}
                sx={{
                  display: 'flex', flexDirection: 'column',
                  px: 1.5, py: 1, cursor: 'pointer',
                  bgcolor: isVisualized ? 'transparent' : 'rgba(192,57,43,0.08)',
                  borderBottom: BORDER_SUBTLE,
                  '&:last-of-type': { borderBottom: 'none' },
                  '&:hover': { bgcolor: BG_CARD },
                  transition: 'background-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <UserAvatar src={avatarUrl} size={32} />
                  <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 600, fontSize: { xs: 11, sm: 13 }, flex: 1 }}>
                    {nickname}
                  </Typography>
                  {!isVisualized && <SmallBadge badgeContent="" color="primary" />}
                </Box>
                {lastMessage && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, pl: '40px' }}>
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontSize: { xs: 10, sm: 12 }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: TEXT_SECONDARY }}>
                        {lastMessage.senderId === currentUser?.id ? 'You: ' : `${lastMessage.sender?.nickname}: `}
                      </Box>
                      {lastMessage.message && lastMessage.message.length > 20
                        ? `${lastMessage.message.slice(0, 15)}...`
                        : lastMessage.message}
                    </Typography>
                    {lastMessage.createdAt && (
                      <Typography variant="caption" sx={{ color: TEXT_SECONDARY, fontSize: 9, flexShrink: 0 }}>
                        {formatDate(lastMessage.createdAt)}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <CircularProgress size={20} sx={{ color: BRAND_RED }} />
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
